<template>
  <v-container fluid>
    <Loading
      :active.sync="loading"
      :is-full-page="isFullPage"
    />
    <v-alert
      v-model="portalAccessRevokedDialog"
      dismissable
      prominent
      type="success"
      transition="scale-transition"
    >
      Portal access successfully revoked!
    </v-alert>
    <v-alert
      v-model="successDialog"
      dismissable
      prominent
      type="success"
      transition="scale-transition"
    >
      Client successfully saved!
    </v-alert>
    <v-alert
      v-model="errorDialog"
      dismissible
      prominent
      type="error"
      transition="scale-transition"
    >
      Something went wrong
    </v-alert>
    <v-alert
      v-model="editing"
      dismissible
      prominent
      type="info"
      transition="scale-transition"
    >
      Editing enabled
    </v-alert>
    <v-alert
      v-model="hasPortalAccess"
      type="info"
      dismissible
      transition="scale-transition"
    >
      {{ client.name }} has portal access.
      Last seen at: {{ lastSeen }}
    </v-alert>
    <v-dialog
      v-model="portalUserDialog"
      max-width="500px"
    >
      <new-user-form
        v-model="portalUserDialog"
        :client-id="clientId"
        @userCreated="addUser"
      />
    </v-dialog>
    <v-row class="ml-2">
      <v-col>
        <span>
          <h1>
            {{ client.name }}
          </h1>
        </span>
        <span v-if="!client.active">
          <h1 style="color: red;">
            Inactive
          </h1>
        </span>
      </v-col>
      <v-spacer />
      <v-col class="mr-2 text-right">
        <v-menu
          top
          origin="center center"
          transition="scale-transition"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              text
              color="primary"
              dark
              v-bind="attrs"
              v-on="on"
            >
              Actions
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="goToClientInventory()">
              Show All Items
            </v-list-item>
            <v-divider />
            <v-list-item @click="toggleEdit()">
              {{ editing ? 'Cancel Edit' : 'Enable Edit' }}
            </v-list-item>
            <v-divider />
            <v-list-item
              v-if="hasPortalAccess"
              @click="revokePortalAccess()"
            >
              Revoke Portal Access
            </v-list-item>
            <v-list-item
              v-else
              @click="addPortalAccess()"
            >
              Grant Portal Access
            </v-list-item>
            <v-divider />
            <v-list-item
              :disabled="!editing"
              @click="save"
            >
              Save
            </v-list-item>
            <v-divider />
            <v-list-item
              v-if="client.active"
              :disabled="client.itemCount !== 0 && client.active"
              @click="deactivateClient"
            >
              Deactivate
            </v-list-item>
            <v-list-item
              v-else
              @click="reactivateClient"
            >
              Reactivate
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        class="col-md-5"
        cols="auto"
      >
        <v-card
          v-if="!loading"
          outlined
          elevation="10"
        >
          <v-card-text>
            <v-text-field
              v-model="client.name"
              label="Name"
              :readonly="!editing"
              dense
            />
            <phone-component
              :input="client.phone"
              :read-only="!editing"
              :required="false"
              @phoneSet="setPhone"
            />
            <v-text-field
              v-model="client.email"
              label="Email"
              :readonly="!editing"
              dense
            />
            <v-text-field
              v-model="client.address1"
              label="Address 1"
              :readonly="!editing"
              dense
            />
            <v-text-field
              v-model="client.address2"
              label="Address 2"
              :readonly="!editing"
              dense
            />
            <v-text-field
              v-model="client.city"
              label="City"
              :readonly="!editing"
              dense
            />
            <state-select
              :input="client.state"
              :read-only="!editing"
              dense
              @stateSelected="setState"
            />
            <v-text-field
              v-model="client.zipCode"
              label="Zip Code"
              :readonly="!editing"
              dense
            />
            <v-text-field
              v-model="client.icnAbbreviation"
              label="ICN Abbreviation"
              readonly
              dense
            />
            <v-slider
              v-model="client.chargeRate"
              label="Storage Rate"
              max="0.25"
              min="0.01"
              step="0.01"
              thumb-label
              :readonly="!editing"
              dense
            >
              <template v-slot:append>
                <v-text-field
                  v-model="client.chargeRate"
                  class="mt-0 pt-0"
                  style="width: 60px;"
                  :readonly="!editing"
                  dense
                />
              </template>
            </v-slider>
            <v-text-field
              v-model="client.storageGracePeriod"
              label="Storage Grace Period"
              type="number"
              :readonly="!editing"
              dense
            />
            <v-slider
              v-model="client.dailyChargeRate"
              label="Daily Minimum Rate"
              max="1.00"
              min="0.01"
              step="0.01"
              thumb-label
              :readonly="!editing"
              dense
            >
              <template v-slot:append>
                <v-text-field
                  v-model="client.dailyChargeRate"
                  vlass="mt-0 pt-0"
                  style="width: 60px"
                  :readonly="!editing"
                  dense
                />
              </template>
            </v-slider>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        class="col-md-5"
        cols="auto"
      >
        <v-row>
          <photo-component
            :owner-id="clientId"
            :edit-enabled="editing"
            default-text="Upload a Client Photo"
          />
        </v-row>
        <v-row>
          <v-container fluid>
            <v-card class="elevation-10">
              <v-textarea
                v-model="client.notes"
                label="Notes"
                prepend-icon="mdi-comment"
                :readonly="!editing"
              />
            </v-card>
          </v-container>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="col-md-10">
        <v-tabs>
          <v-tab>
            Subclients
          </v-tab>
          <v-tab>
            Contacts
          </v-tab>
          <v-tab>
            All Inventory
          </v-tab>
          <v-tab>
            Pending Inventory
          </v-tab>
          <v-tab>
            Most Recent Inventory
          </v-tab>
          <v-tab-item>
            <v-card class="elevation-10">
              <v-card-text>
                <v-data-table
                  :headers="headers"
                  :items="subClients"
                  :items-per-page="-1"
                  :search="subClientSearch"
                  :loading="subClientLoading"
                  dense
                  loading-text="loading, please wait"
                  @click:row="goToInventory"
                >
                  <template v-slot:top>
                    <v-text-field
                      v-model="subClientSearch"
                      prepend-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                    />
                    <v-switch
                      v-model="includeInactive"
                      label="Include Subclients with no active inventory"
                      color="primary"
                    />
                  </template>
                  <template v-slot:item.totalToDate="{ item }">
                    <span>{{ item.totalToDate | currency }}</span>
                  </template>
                  <template v-slot:item.dailyDelta="{ item}">
                    <span>{{ item.dailyDelta | currency }}</span>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tab-item>
          <v-tab-item>
            <v-card class="elevation-10">
              <client-contacts :client-id="clientId" />
            </v-card>
          </v-tab-item>
          <v-tab-item>
            <v-card class="elevation-10">
              <inventory-list
                :client-id="clientId"
                :hide-filters="true"
              />
            </v-card>
          </v-tab-item>
          <v-tab-item>
            <v-card class="elevation-10">
              <pending-inventory-list :client-id="clientId" />
            </v-card>
          </v-tab-item>
          <v-tab-item>
            <v-card class="elevation-10">
              <inventory-list
                :client-id="clientId"
                :hide-filters="true"
                :most-recent="true"
              />
            </v-card>
          </v-tab-item>
        </v-tabs>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="col-md-10">
        <v-tabs>
          <v-tab>
            Receiving Email Recipients
          </v-tab>
          <v-tab-item>
            <v-card class="elevation-10">
              <v-data-table
                :headers="emailRecipientHeaders"
                :items="emailRecipients"
                :items-per-page="-1"
                :loading="recipientsLoading"
                dense
              >
                <template v-slot:top>
                  <v-dialog
                    v-model="newRecipientDialog"
                    max-width="600px"
                    @click:outside="closeNewRecipientDialog"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        color="primary"
                        class="mb-2"
                        v-bind="attrs"
                        v-on="on"
                      >
                        New Email Recipient
                      </v-btn>
                    </template>
                    <v-card>
                      <v-card-text>
                        <v-container>
                          <v-text-field
                            v-model="newRecipient.emailAddress"
                            label="Email Address"
                          />
                        </v-container>
                      </v-card-text>
                      <v-card-actions>
                        <v-spacer />
                        <v-btn
                          color="blue darken-1"
                          text
                          @click="saveNewRecipient"
                        >
                          Save
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-icon
                    small
                    @click.stop="deleteRecipient(item)"
                    @click.stop.prevent
                  >
                    mdi-delete
                  </v-icon>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>
        </v-tabs>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

import PhoneComponent from './PhoneComponent.vue';
import StateSelect from './StateSelectComponent.vue';
import ClientContacts from './ClientContactList.vue';
import PhotoComponent from './PhotoComponent.vue';
import NewUserForm from './NewUserForm.vue';
import InventoryList from './InventoryList.vue';
import PendingInventoryList from './PendingInventoryList.vue';

export default {
  name: 'ClientEditComponent',
  components: {
    PhoneComponent,
    Loading,
    StateSelect,
    ClientContacts,
    PhotoComponent,
    NewUserForm,
    InventoryList,
    PendingInventoryList,
  },
  props: {
    clientId: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    subClientLoading: false,
    includeInactive: false,
    client: {},
    isFullPage: true,
    portalUserDialog: false,
    portalAccessRevokedDialog: false,
    editing: false,
    loading: false,
    successDialog: false,
    errorDialog: false,
    subClients: [],
    subClientSearch: '',
    hasPortalAccess: false,
    recipientsLoading: false,
    newRecipient: {},
    newRecipientDialog: false,
    emailRecipientHeaders: [
      {
        text: 'Email Address',
        value: 'emailAddress',
      },
      {
        text: 'Actions',
        value: 'actions',
        sortable: false,
      },
    ],
    emailRecipients: [],
    headers: [
      {
        text: 'Name',
        value: 'name',
      },
      {
        text: 'ICN Abbreviation',
        value: 'icnAbbreviation',
      },
      {
        text: 'Pieces',
        value: 'itemCount',
        filterable: false,
      },
      {
        text: 'Storage Total to Date',
        value: 'totalToDate',
        filterable: false,
      },
      {
        text: 'Current Daily Rate Increase',
        value: 'dailyDelta',
      },
      {
        text: 'Total Cubic Feet',
        value: 'totalCubicFeet',
        filterable: true,
      },
    ],
  }),
  computed: {
    lastSeen() {
      if (this.client.lastSeen == null) {
        return 'Never';
      }

      return new Date(this.client.lastSeen);
    },
  },
  watch: {
    includeInactive(val) {
      this.getSubClientPairs(val);
    },
  },
  mounted() {
    this.client = this.getClient();
    this.getSubClientPairs(this.includeInactive);
    this.checkPortalAccess();
    this.getEmailRecipients();
  },
  methods: {
    ...mapActions('apiClient', ['get', 'update', 'del', 'create']),
    setPhone(val) {
      this.client.phone = val;
    },
    setState(val) {
      this.client.state = val;
    },
    goToClientInventory() {
      this.$router.push({ name: 'inventoryList', params: { clientId: this.client.clientId, subClientId: this.client.clientId } });
    },
    toggleEdit() {
      this.editing = !this.editing;
    },
    addPortalAccess() {
      this.portalUserDialog = true;
    },
    addUser(newUser) {
      this.$router.push({ name: 'userEdit', params: { inputUser: newUser, userId: newUser.userId } });
    },
    async getClient() {
      this.loading = true;

      await this.get({ url: '/clients', id: this.clientId }).then(
        (response) => {
          this.client = response.data;
        },
        (error) => {
          console.log(error);
        },
      );

      this.loading = false;
    },
    goToInventory(props) {
      this.$router.push({
        name: 'inventoryList',
        params: {
          pairClientId: this.client.clientId,
          pairSubClientId: props.subClientId,
          useDefaultLocation: false,
          useDefaultBillingStatus: false,
        },
      });
    },
    async getSubClientPairs(includeInactive) {
      this.subClientLoading = true;
      await this.get({ url: `/clients/${this.clientId}/subClients?client=true&includeInactiveInventory=${includeInactive}` }).then(
        (response) => {
          this.subClients = response.data;
          this.subClientLoading = false;
        },
        (error) => {
          console.log(error);
          this.subClientLoading = false;
        },
      );
    },
    async getEmailRecipients() {
      this.recipientsLoading = true;
      await this.get({ url: `clients/${this.clientId}/emailRecipients` }).then(
        (response) => {
          this.emailRecipients = response.data;
          this.recipientsLoading = false;
        },
        (error) => {
          this.recipientsLoading = false;
          console.log(error);
        },
      );
    },
    async checkPortalAccess() {
      await this.get({ url: `/users/client/${this.clientId}` }).then(
        (response) => {
          this.hasPortalAccess = response.data.used;
        },
        (error) => {
          console.log(error);
        },
      );
    },
    async revokePortalAccess() {
      await this.del({ url: `/users/client/${this.clientId}` }).then(
        () => {
          this.portalAccessRevokedDialog = true;
          this.checkPortalAccess();
        },
        (error) => {
          console.log(error);
        },
      );
    },
    closeNewRecipientDialog() {
      this.newRecipientDialog = false;
    },
    saveNewRecipient() {
      this.newRecipient.clientId = this.clientId;
      this.create({ url: '/clients/emailRecipients', body: this.newRecipient }).then(
        () => {
          this.newRecipientDialog = false;
          this.newRecipient = {};
          this.getEmailRecipients();
        },
        (error) => {
          console.log(error);
        },
      );
    },
    deleteRecipient(recipient) {
      this.del({ url: `/clients/emailRecipients/${recipient.id}` }).then(
        (response) => {
          if (response.data.successful) {
            this.getEmailRecipients();
          }
        },
        (error) => {
          console.log(error);
        },
      );
    },
    deactivateClient() {
      this.client.active = false;
      this.save();
    },
    reactivateClient() {
      this.client.active = true;
      this.save();
    },
    save() {
      this.update({ url: '/clients', body: this.client, id: this.client.clientId }).then(
        (response) => {
          this.successDialog = true;
          this.client = response.data;
          setTimeout(() => {
            this.successDialog = false;
          }, 5000);
          this.editing = false;
          this.$router.go();
        },
        (error) => {
          this.errorDialog = true;
          console.log(error);
          setTimeout(() => {
            this.errorDialog = false;
          }, 5000);
        },
      );
    },
  },
};
</script>
